// src-lit/components/collection/kd-collection-products.ts
/**
 * kd-collection-products
 *
 * Storefront API (GraphQL) を使用してコレクション内の商品一覧を取得し、
 * 無限スクロールで表示する。
 *
 * @element kd-collection-products
 *
 * @attr {string} collection-handle - コレクションハンドル（必須）
 * @attr {string} storefront-url - Storefront API エンドポイント（必須）
 * @attr {string} storefront-token - Storefront Access Token（必須）
 * @attr {number} page-size - 1回の取得件数（デフォルト: 20）
 *
 * @example
 * ```html
 * <kd-collection-products
 *   collection-handle="food"
 *   storefront-url="https://example.myshopify.com/api/2026-01/graphql.json"
 *   storefront-token="xxxxx"
 *   page-size="20"
 * >
 *   <div class="kd-collection-products__grid"></div>
 *   <div class="kd-collection-products__loading" hidden>読み込み中...</div>
 *   <div class="kd-collection-products__sentinel"></div>
 * </kd-collection-products>
 * ```
 */

import { LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { EVENT_NAMES } from '../../constants/events.js';
import { SELECTORS } from '../../constants/selectors.js';
import {
  buildGraphQLQuery,
  parseProductNodes,
  getPageInfo,
  formatPrice,
  type ProductDisplayData,
} from './collection.logic.js';

@customElement('kd-collection-products')
export class KdCollectionProducts extends LitElement {
  createRenderRoot() {
    return this;
  }

  // ========================================
  // Properties (HTML 属性)
  // ========================================
  @property({ type: String, attribute: 'collection-handle' })
  collectionHandle = '';

  @property({ type: String, attribute: 'storefront-url' })
  storefrontUrl = '';

  @property({ type: String, attribute: 'storefront-token' })
  storefrontToken = '';

  @property({ type: Number, attribute: 'page-size' })
  pageSize = 20;

  // ========================================
  // State (内部状態)
  // ========================================
  @state()
  private _isLoading = false;

  @state()
  private _hasNextPage = true;

  @state()
  private _endCursor: string | null = null;

  @state()
  private _error: string | null = null;

  // ========================================
  // Private fields
  // ========================================
  private _observer: IntersectionObserver | null = null;

  private _gridElement: HTMLElement | null = null;

  private _loadingElement: HTMLElement | null = null;

  private _sentinelElement: HTMLElement | null = null;

  private _initRetryCount = 0;

  private static readonly MAX_INIT_RETRIES = 10;

  private static readonly INIT_RETRY_INTERVAL = 100; // ms

  // ========================================
  // Lifecycle
  // ========================================
  override connectedCallback(): void {
    super.connectedCallback();
    this._initRetryCount = 0;
    this._tryInitialize();
  }

  /**
   * 子要素が見つかるまでリトライして初期化
   * Theme Editor での再描画時、子要素がまだパースされていない場合がある
   */
  private _tryInitialize(): void {
    this._initElements();

    // grid 要素が見つかれば初期化完了
    if (this._gridElement) {
      this._setupIntersectionObserver();
      this._loadInitialProducts();
      return;
    }

    // リトライ上限チェック
    if (this._initRetryCount >= KdCollectionProducts.MAX_INIT_RETRIES) {
      console.warn('[kd-collection-products] 子要素が見つかりませんでした');
      return;
    }

    // リトライ
    this._initRetryCount++;
    setTimeout(() => this._tryInitialize(), KdCollectionProducts.INIT_RETRY_INTERVAL);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._cleanupObserver();
  }

  // ========================================
  // Render (nothing を返す: DOM 操作のみ)
  // ========================================
  override render(): typeof nothing {
    return nothing;
  }

  // ========================================
  // Private methods
  // ========================================
  private _initElements(): void {
    this._gridElement = this.querySelector(SELECTORS.COLLECTION.GRID);
    this._loadingElement = this.querySelector(SELECTORS.COLLECTION.LOADING);
    this._sentinelElement = this.querySelector(SELECTORS.COLLECTION.SENTINEL);
  }

  private _setupIntersectionObserver(): void {
    if (!this._sentinelElement) {
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !this._isLoading && this._hasNextPage) {
          this._loadMoreProducts();
        }
      },
      {
        rootMargin: '200px', // 200px 手前で発火
      }
    );

    this._observer.observe(this._sentinelElement);
  }

  private _cleanupObserver(): void {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  private async _loadInitialProducts(): Promise<void> {
    await this._fetchProducts(null);
  }

  private async _loadMoreProducts(): Promise<void> {
    await this._fetchProducts(this._endCursor);
  }

  private async _fetchProducts(cursor: string | null): Promise<void> {
    if (this._isLoading || !this._hasNextPage) {
      return;
    }

    if (!this.collectionHandle || !this.storefrontUrl || !this.storefrontToken) {
      this._error = '必須属性が設定されていません';
      console.error('[kd-collection-products] Missing required attributes');
      return;
    }

    this._isLoading = true;
    this._showLoading(true);

    try {
      const query = buildGraphQLQuery(
        this.collectionHandle,
        this.pageSize,
        cursor
      );

      const response = await fetch(this.storefrontUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const data = await response.json();

      if (data.errors?.length) {
        throw new Error(data.errors[0].message);
      }

      const products = parseProductNodes(data);
      const pageInfo = getPageInfo(data);

      this._hasNextPage = pageInfo.hasNextPage;
      this._endCursor = pageInfo.endCursor;

      this._renderProducts(products);
      this._dispatchLoadedEvent(products.length);
    } catch (error) {
      this._error = error instanceof Error ? error.message : '不明なエラー';
      console.error('[kd-collection-products] Fetch error:', error);
    } finally {
      this._isLoading = false;
      this._showLoading(false);
    }
  }

  private _showLoading(show: boolean): void {
    if (this._loadingElement) {
      this._loadingElement.hidden = !show;
    }
  }

  private _renderProducts(products: ProductDisplayData[]): void {
    if (!this._gridElement) {
      return;
    }

    const fragment = document.createDocumentFragment();

    for (const product of products) {
      const card = this._createProductCard(product);
      fragment.appendChild(card);
    }

    this._gridElement.appendChild(fragment);
  }

  private _createProductCard(product: ProductDisplayData): HTMLElement {
    const article = document.createElement('article');
    article.className =
      'w-full min-w-0 bg-white rounded-lg overflow-hidden shadow-sm';
    article.dataset.productId = product.id;

    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'block no-underline text-inherit';

    // 画像
    if (product.imageUrl) {
      const img = document.createElement('img');
      img.src = product.imageUrl;
      img.alt = product.imageAlt;
      img.className = 'block w-full h-auto object-cover aspect-square';
      img.loading = 'lazy';
      img.width = 300;
      img.height = 200;
      link.appendChild(img);
    }

    // タイトル
    const title = document.createElement('h3');
    title.className =
      'text-sm font-medium m-2 leading-snug line-clamp-2';
    title.textContent = product.title;
    link.appendChild(title);

    // 価格
    const priceWrapper = document.createElement('div');
    priceWrapper.className = 'px-2 pb-2';

    if (product.compareAtPrice !== null && product.discountRate !== null) {
      const originalPrice = document.createElement('span');
      originalPrice.className = 'text-xs text-gray-400 line-through mr-1';
      originalPrice.textContent = formatPrice(product.compareAtPrice);
      priceWrapper.appendChild(originalPrice);

      const discountBadge = document.createElement('span');
      discountBadge.className =
        'inline-block bg-red-600 text-white text-[10px] px-1 py-0.5 rounded-sm ml-1';
      discountBadge.textContent = `${product.discountRate}% OFF`;
      priceWrapper.appendChild(discountBadge);
    }

    const currentPrice = document.createElement('span');
    currentPrice.className = 'text-base font-bold text-red-600';
    currentPrice.textContent = formatPrice(product.price);
    priceWrapper.appendChild(currentPrice);

    link.appendChild(priceWrapper);

    // 在庫なしバッジ
    if (!product.availableForSale) {
      const soldOut = document.createElement('span');
      soldOut.className =
        'block bg-gray-500 text-white text-center py-1 text-xs';
      soldOut.textContent = '売り切れ';
      link.appendChild(soldOut);
    }

    article.appendChild(link);
    return article;
  }

  private _dispatchLoadedEvent(count: number): void {
    this.dispatchEvent(
      new CustomEvent(EVENT_NAMES.COLLECTION.PRODUCTS_LOADED, {
        bubbles: true,
        composed: true,
        detail: {
          count,
          hasNextPage: this._hasNextPage,
          collectionHandle: this.collectionHandle,
        },
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-collection-products': KdCollectionProducts;
  }
}
